/**
 * useDemoPlayerView.ts
 *
 * One line between a player surface and the demo clock (card D2): the clock's
 * reactive view (`composables/useDemoClock.ts`) plus the PLAYER's own reading of
 * it — `demoPlayerModel()` with the reader's language and the app's admin floor.
 *
 * The three surfaces (the FAB pill, its mini player, Settings → Demo →
 * Playback) all show the same clock and must agree on every label and gate, so
 * the reading is written once here instead of three times in three templates.
 * It is a composable and not a store because it holds no state of its own: the
 * status lives in `useDemoClock`'s module mirror, and this only derives from it.
 */
import { computed, watch, type ComputedRef } from 'vue';

import { useDemoClock, type DemoClockView } from './useDemoClock';
import { useAuthStore } from '../stores/auth';
import { useWebRTCStore } from '../stores/webrtc';
import { isAdminRole } from '../utils/roles';
import { localeTag } from '../utils/formatDate';
import { tt } from '../utils/consoleText';
import { demoPlayerModel, type DemoPlayerModel } from '../utils/demoPlayerModel';

export interface DemoPlayerViewOptions {
  /** Re-read the hub's status once a second while this surface is open. */
  poll?: boolean;
  /** This hub can jump days (`playback {action:'day'}`) — false until D8a. */
  daySeekEnabled?: boolean;
}

export interface DemoPlayerView {
  /** The clock itself — also what a surface with no status yet still reads. */
  demo: DemoClockView;
  /** The player's labels and gates, or `null` until the hub has answered. */
  model: ComputedRef<DemoPlayerModel | null>;
  /** The app's own admin floor (`isAdminRole`), which the hub also enforces. */
  canControl: ComputedRef<boolean>;
  /** Is there a player at all? (demo mode on AND the hub wants it shown) */
  visible: ComputedRef<boolean>;
  /**
   * Does this hub's demo REPLACE the agent FAB? (plan §4.8's `replaceAgentFab`,
   * defaulting on). The layout reads it to decide which FAB owns the corner, so
   * the decision cannot be spelled in two places with two defaults.
   */
  replaceAgentFab: ComputedRef<boolean>;
  /** `Summer · Day 1`, or the script's own day name when it sends one. */
  dayText: ComputedRef<string>;
  /** `Tue 14 Jul · 08:05 · Summer · Day 1` — the FAB's collapsed pill. */
  pillText: ComputedRef<string>;
}

export function useDemoPlayerView(options: DemoPlayerViewOptions = {}): DemoPlayerView {
  const demo = useDemoClock({ poll: options.poll === true });
  const auth = useAuthStore();
  const webrtc = useWebRTCStore();

  /**
   * Ask the hub once there IS a hub session. Mounting alone must not: the
   * desktop launcher's home and the cloud landing pages mount this layout with
   * no hub behind them, and a `GET /api/demo` there would be a 401 on a page
   * that is not even talking to a hub (the same reason the launcher holds no hub
   * connection at all). A restored session or a WebRTC hub session flips this
   * true, and the status arrives then.
   */
  const connected = computed(() => auth.isAuthenticated || webrtc.connected);
  watch(
    connected,
    (ready) => {
      if (ready) void demo.ensureLoaded();
    },
    { immediate: true },
  );

  const canControl = computed(() => isAdminRole(auth.user?.role));

  const model = computed<DemoPlayerModel | null>(() => {
    const status = demo.status.value;
    if (!status) return null;
    return demoPlayerModel(status, {
      canControl: canControl.value,
      locale: localeTag(),
      daySeekEnabled: options.daySeekEnabled === true,
    });
  });

  // The FAB's own gate (plan §4.8): demo mode on AND `showFab`. The settings page
  // does NOT use this — an operator needs the page whether or not a FAB is wanted.
  const visible = computed(() => model.value?.enabled === true && demo.showFab.value);

  /**
   * The day, worded once for every surface: the script's own name for it when
   * the hub sends one (`Work day 1/3`), else `Day 1`, with the season in front
   * when the clock names a season. D0 sends neither yet, so today this reads
   * `Day 1` — and it grows into the plan's `Summer 1/3` with no template change.
   */
  const dayText = computed(() => {
    const reading = model.value;
    if (!reading) return '';
    const season =
      reading.season === 'summer'
        ? tt('Summer', 'Poletje')
        : reading.season === 'winter'
          ? tt('Winter', 'Zima')
          : '';
    const day = reading.dayName
      ? [reading.dayName, reading.dayFraction].filter(Boolean).join(' ')
      : (reading.dayFraction ?? tt(`Day ${reading.dayNumber}`, `Dan ${reading.dayNumber}`));
    return [season, day].filter(Boolean).join(' · ');
  });

  /** The collapsed pill: the date, the sim time, the day — the plan's sketch. */
  const pillText = computed(() => {
    const reading = model.value;
    if (!reading) return '';
    return [reading.dateLabel, reading.clockLabel, dayText.value].filter(Boolean).join(' · ');
  });

  return { demo, model, canControl, visible, replaceAgentFab: demo.replaceAgentFab, dayText, pillText };
}
